/**
 * Leads API service
 * Feature: 015-manual-crud-entities - User Story 2
 * Feature: 020-aws-production-readiness - Updated to route through API Gateway
 */

import axios from 'axios';
import type {
  Lead,
  CreateLeadRequest,
  UpdateLeadRequest,
} from '../types/lead';

// Create axios instance with auth headers - routes through API Gateway at /api
const axiosInstance = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

axiosInstance.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('id_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (config.data) {
    // 1. Clean regular flat contact phones in lead payloads
    if (typeof config.data.contactPhone === 'string') {
      config.data.contactPhone = config.data.contactPhone.replace(/[^\d+]/g, '');
    }
    // 2. Clean inline nested account creation phones
    if (config.data.newAccount && typeof config.data.newAccount.phone === 'string') {
      config.data.newAccount.phone = config.data.newAccount.phone.replace(/[^\d+]/g, '');
    }
  }

  return config;
});

/**
 * Create a new lead with optional account creation
 * Supports two modes:
 * 1. Link to existing account (provide accountId)
 * 2. Create new account inline (provide newAccount)
 */
export async function createLead(request: CreateLeadRequest): Promise<Lead> {
  const response = await axiosInstance.post<Lead>('/leads', request);
  return response.data;
}

/**
 * Get lead by ID
 * @returns Lead with ETag in response headers
 */
export async function getLeadById(id: string): Promise<{ lead: Lead; etag: string }> {
  const response = await axiosInstance.get<Lead>(`/leads/${id}`);
  
  const etag = response.headers['etag'] || '';
  return { lead: response.data, etag };
}

/**
 * Update an existing lead
 * @param id Lead ID (Guid)
 * @param request Update payload with all required fields
 * @param etag ETag from previous GET/POST for optimistic concurrency control
 * @returns Updated lead with new ETag
 * @throws 412 Precondition Failed if lead was modified by another user
 */
export async function updateLead(
  id: string,
  request: UpdateLeadRequest,
  etag?: string
): Promise<{ lead: Lead; etag: string }> {
  const headers: Record<string, string> = {};
  if (etag) {
    headers['If-Match'] = etag;
  }

  const response = await axiosInstance.put<Lead>(
    `/leads/${id}`,
    request,
    { headers }
  );

  const newEtag = response.headers['etag'] || '';
  return { lead: response.data, etag: newEtag };
}

/**
 * List leads with optional filtering
 */
export async function listLeads(params?: {
  status?: number;
  ownerId?: string;
  leadSource?: string;
  pageNumber?: number;
  pageSize?: number;
}): Promise<{ leads: Lead[]; totalCount: number }> {
  const response = await axiosInstance.get<{ leads: Lead[]; totalCount: number }>(
    '/leads',
    { params }
  );
  return response.data;
}

/**
 * Associate or unassociate a client with a lead (Feature 018 US1)
 * @param leadId Lead ID (Guid)
 * @param accountId Account ID to associate (null to unassociate)
 * @param etag ETag from previous GET for optimistic concurrency control
 * @returns Updated lead with new ETag
 * @throws 409 Conflict if lead was modified by another user
 */
export async function associateLeadWithClient(
  leadId: string,
  accountId: string | null,
  etag?: string
): Promise<{ lead: Lead; etag: string }> {
  const headers: Record<string, string> = {};
  if (etag) {
    headers['If-Match'] = etag;
  }

  const response = await axiosInstance.patch<Lead>(
    `/leads/${leadId}/client`,
    { clientId: accountId },
    { headers }
  );

  const newEtag = response.headers['etag'] || '';
  return { lead: response.data, etag: newEtag };
}

/**
 * Update lead status (Feature 019 US2/P2)
 * @param leadId Lead ID (Guid)
 * @param status New status value
 * @returns Updated lead info with status and timestamps
 */
export async function updateLeadStatus(
  leadId: string,
  status: string
): Promise<{
  leadId: string;
  status: string;
  updatedAt: string;
  qualifiedAt?: string | null;
  closedAt?: string | null;
}> {
  const response = await axiosInstance.patch(
    `/leads/${leadId}/status`,
    { status }
  );

  return response.data;
}
